import { useForm } from "@tanstack/react-form";
import { z } from "zod";
import { citizenshipOptions } from "../utils/citizenship";
import { paygTypes, paymentFrequencies } from "../types";

// Helper schemas for reusable patterns
const abnRegex = /^\d{11}$/;

// Australian states enum
const australianStates = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "NT", "ACT"] as const;

// US states enum
const usStates = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
    "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
    "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
    "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
    "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
] as const;

// Income types
// const incomeTypes = [
//     "PAYG income",
//     "Rental income",
//     "Government Pension / Social Security",
//     "Child Support",
//     "Investment",
//     "Superannuation",
//     "Defence Income (Reserve)"
// ] as const;

// Rental income subtypes
const rentalTypes = ["new", "existing", "holiday", "shared_space", "secondary_dwelling"] as const;

// Self-employed business structures
const businessStructures = ["Sole Trader", "Partnership", "Company", "Trust"] as const;

// Defence verification methods
const defenceVerificationMethods = ["last_12_months_credits", "minimum_days_calculation"] as const;

// Document types for income
// const incomeDocumentTypes = ["Payslip", "Bank Statement", "Tax Return", "Letter of Offer"] as const;

// Payment frequency
// const paymentFrequencies = ["Weekly", "Fortnightly", "Monthly", "Annually"] as const;

// Product types
const productTypes = ["home-loan", "personal-loan", "business-loan", "investment-loan"] as const;

// Goal types
const goalTypes = ["buying-new-property", "switch-home-loan", "existing-home-loan", "equity-line-of-credit"] as const;

// Purpose of loan options
const loanPurposes = [
    "home-purchase",
    "refinancing",
    "investment-property",
    "renovation",
    "debt-consolidation",
    "personal",
    "business",
    "other",
] as const;

const validCitizenshipOptions: string[] = citizenshipOptions.map(c => c.value!);

const referenceSchema = z.object({
    boundingBoxes: z.array(z.any()),
    fileId: z.number(),
    fileName: z.string(),
    validationStatus: z.enum(["ok", "warning", "error"]),
    validationStatusReason: z.string().nullable(),
    reasoning: z.string().nullable()
});

const loanApplicationSchema = z.object({
    applicationId: z.number().optional(),
    submitted: z.boolean().default(false),
    submissionId: z.string().optional(), // Unique ID for each submission attempt
    apiSubmitted: z.boolean().default(false), // Track if API submission has been sent

    // Outcome processing state
    outcome: z.any().optional(), // Will hold the OutcomeData when received
    isLoadingOutcome: z.boolean().default(false),
    hasOutcomeTimedOut: z.boolean().default(false),

    productType: z.enum(productTypes, {
        errorMap: () => ({ message: "Please select a valid product type" }),
    }),

    goalType: z.enum(goalTypes, {
        errorMap: () => ({ message: "Please select a valid goal type" }),
    }),

    purposeOfLoan: z.enum(loanPurposes, {
        errorMap: () => ({ message: "Please select a valid loan purpose" }),
    }),

    // Consent fields
    consentProvision: z.enum(["now", "already"], {
        errorMap: () => ({ message: "Please select when you are providing consent" }),
    }),
    consentDocument: z
        .array(
            z.object({
                id: z.number(),
                fileName: z.string(),
                status: z.enum(["success", "uploading", "error"]),
                uploadedAt: z.string().optional(),
            })
        )
        .default([]),
    consentType: z
        .enum(["verbal", "written"], {
            errorMap: () => ({ message: "Please select the type of consent provided" }),
        })
        .optional(),
    existingConsentDocument: z
        .array(
            z.object({
                id: z.number(),
                fileName: z.string(),
                status: z.enum(["success", "uploading", "error"]),
                uploadedAt: z.string().optional(),
            })
        )
        .default([]),

    propertyAddress: z
        .string()
        .min(1, "Property address is required")
        .max(200, "Property address must be less than 200 characters"),
    amounts: z
        .object({
            propertyMarketValue: z.number().min(0, "Property market value must be positive"),
            loanAmount: z.number().min(0, "Loan amount must be positive"),
            downPayment: z.number().min(0, "Down payment must be positive"),
            downPaymentPercentage: z
                .number()
                .min(0, "Down payment percentage must be positive")
                .max(100, "Down payment percentage cannot exceed 100%")
        })
        .superRefine((values, ctx) => {
            const { propertyMarketValue, loanAmount, downPayment } = values;
            // Loan amount cannot exceed property value
            if (loanAmount > propertyMarketValue && propertyMarketValue > 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["loanAmount"],
                message: "Loan amount cannot exceed property value",
            });
            }

            // Down payment must be >= 20% of property value
            if (propertyMarketValue > 0 && downPayment < propertyMarketValue * 0.2) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["downPayment"],
                message: "Down payment must be at least 20% of property value",
            });
        }
    }),
    documents: z
        .array(
            z.object({
                id: z.number(),
                fileName: z.string(),
                status: z.enum(["success", "uploading", "error"]),
                uploadedAt: z.string().optional(),
                // Store full document content when available
                filename: z.string().optional(),
                originalName: z.string().optional(),
                mimeType: z.string().optional(),
                size: z.number().optional(),
                contentBase64: z.string().optional(),
                documentValidation: z.array(z.any()).optional(),
            })
        )
        .default([]),

    uploadStates: z
        .array(
            z.object({
                status: z.enum(["idle", "uploading", "success", "error", "processing"]),
                progress: z.number(),
                fileName: z.string(),
                documentId: z.number().optional(),
                file: z.instanceof(File).optional(),
                size: z.number().optional(),
                error: z.string().optional(),
                uploadedAt: z.number().optional(),
            })
        )
        .default([]),
    applicants: z
        .array(
            z.object({
                id: z.string().default(() => Math.random().toString(36).substr(2, 9)),
                givenName: z
                .string()
                .min(1, "Given names are required")
                .max(100, "Given names must be less than 100 characters")
                .regex(
                    /^[a-zA-Z\s'-]+$/,
                    "Given names can only contain letters, spaces, hyphens, and apostrophes"
                ),
                familyName: z
                .string()
                .min(1, "Family names are required")
                .max(50, "Family names must be less than 50 characters")
                .regex(
                    /^[a-zA-Z\s'-]+$/,
                    "Family names can only contain letters, spaces, hyphens, and apostrophes"
                ),
                dateOfBirth: z.string().date().optional().or(z.literal("")),
                citizenship: z
                .string().refine(value => validCitizenshipOptions.includes(value), {
                    message: "Please select a valid citizenship"
                })
                    .optional(),
                phoneNumber: z.string().length(10, "Invalid phone number").optional().or(z.literal("")),
                email: z.string().email({message: "Invalid email address"}).optional().or(z.literal("")),
                postCode: z.string().length(4, "Invalid post code").optional().or(z.literal("")),
                country: z.string().optional().or(z.literal("")),
                suburb: z.string().optional().or(z.literal("")),
                city: z.string().optional().or(z.literal("")),
                state: z.enum([...australianStates, ...usStates]).optional(),
                addressLine1: z.string().optional().or(z.literal("")),
                addressLine2: z.string().optional().or(z.literal("")),
                proofOfIdentity: z
                    .enum(["Driver's License", "Passport", "Birth Certificate", "National ID Card"], {
                        errorMap: () => ({ message: "Please select a valid proof of identity" }),
                    })
                    .optional(),
                idValue: z.string().min(1, "ID value is required"),
                isIncomplete: z.boolean().optional(),
                references: z.map(z.string(), referenceSchema).default(new Map()),
            })
        )
        .default([]),

    // Employments per applicant (for multi-applicant Employment step)
    employments: z
        .array(
            z.object({
                id: z.string().default(() => Math.random().toString(36).substr(2, 9)),
                applicantId: z.string(),
                employerName: z.string().optional().or(z.literal("")),
                businessAbn: z.string().optional().or(z.literal("")),
                jobTitle: z.string().optional().or(z.literal("")),
                employmentType: z.enum([
                    "Permanent Employee",
                    "Permanent Employee (Medical & Emergency Services)",
                    "Director's Wages",
                    "Casual / Contractor / Gig Economy",
                    "Uncertain (Commission, Overtime)",
                    "Uncertain (Bonus)",
                    "Contractor"
                ]).optional(),
                employmentStartDate: z.string().optional().or(z.literal("")),
                employmentEndDate: z.string().optional().or(z.literal("")),
                isIncomplete: z.boolean().optional(),
                references: z.map(z.string(), referenceSchema).default(new Map()),
            })
        )
        .default([]),
    incomes: z
        .array(
            z.discriminatedUnion("incomeType", [
                // PAYG Income
                z.object({
                    id: z.string().default(() => Math.random().toString(36).substr(2, 9)),
                    applicantId: z.string(),
                    incomeType: z.literal("PAYG income"),
                    employerId: z.string().optional(),

                    paygType: z.enum(paygTypes, {
                        errorMap: () => ({ message: "Please select a valid PAYG income type" }),
                    }),

                    documentIssueDate: z
                        .string()
                        .min(1, "Document issue date is required")
                        .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format"),

                    employerName: z
                        .string()
                        .min(1, "Employer name is required")
                        .max(100, "Employer name must be less than 100 characters"),

                    payFrequency: z.enum(paymentFrequencies, {
                        errorMap: () => ({ message: "Please select a valid pay frequency" }),
                    }),

                    isMedicalEmergencyOccupation: z.boolean().default(false),

                    currency: z.string().min(3).max(3).default("AUD"),

                    // Pay cycle details - simplified for now, can be expanded later
                    payments: z
                        .array(
                            z.object({
                                id: z.string().default(() => crypto.randomUUID()),
                                paygIncomeId: z.string().optional(),
                                payDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format"),
                                grossPay: z.number().min(0, "Gross pay must be positive"),
                                netPay: z.number().min(0, "Net pay must be positive"),
                                baseWage: z.number().min(0, "Base wage must be positive"),
                                ytdGross: z.number().min(0).optional(),
                                overtimeAmount: z.number().min(0).optional(),
                                commissionAmount: z.number().min(0).optional(),
                                bonusAmount: z.number().min(0).optional(),
                            })
                        )
                        .min(1, "At least one payment entry is required")
                        .default([]),

                    documents: z
                        .array(
                            z.object({
                                id: z.number(),
                                fileName: z.string(),
                                status: z.enum(["success", "uploading", "error"]),
                                uploadedAt: z.string().optional(),
                            })
                        )
                        .default([]),
                    references: z.map(z.string(), referenceSchema).default(new Map()),
                }),

                // Rental Income
                z.object({
                    id: z.string().default(() => Math.random().toString(36).substr(2, 9)),
                    applicantId: z.string(),
                    incomeType: z.literal("Rental income"),

                    rentalType: z.enum(rentalTypes, {
                        errorMap: () => ({ message: "Please select a valid rental type" }),
                    }),

                    propertyAddress: z
                        .string()
                        .min(1, "Property address is required")
                        .max(200, "Property address must be less than 200 characters"),

                    documentIssueDate: z
                        .string()
                        .min(1, "Document issue date is required")
                        .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format"),

                    currency: z.string().min(3).max(3).default("AUD"),

                    // Fields for regular rentals
                    rentalAmount: z.number().min(0).optional(),
                    paymentFrequency: z.enum(["weekly", "monthly"]).optional(),

                    // Field for new rental appraisal
                    appraisedRentRangeLow: z.number().min(0).optional(),

                    // Fields for holiday/shared space rental
                    annualStatementPeriodEnd: z.string().optional(),
                    totalIncomeForPeriod: z.number().min(0).optional(),

                    documents: z
                        .array(
                            z.object({
                                id: z.number(),
                                fileName: z.string(),
                                status: z.enum(["success", "uploading", "error"]),
                                uploadedAt: z.string().optional(),
                            })
                        )
                        .default([]),
                    references: z.map(z.string(), referenceSchema).default(new Map()),
                }),

                // Government Pension / Social Security
                z.object({
                    id: z.string().default(() => Math.random().toString(36).substr(2, 9)),
                    applicantId: z.string(),
                    incomeType: z.literal("Government Pension / Social Security"),

                    paymentType: z
                        .string()
                        .min(1, "Payment type is required")
                        .max(100, "Payment type must be less than 100 characters"),

                    paymentsReceived: z
                        .array(
                            z.object({
                                id: z.string().default(() => crypto.randomUUID()),
                                date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format"),
                                amount: z.number().min(0, "Amount must be positive"),
                            })
                        )
                        .min(2, "Minimum of two payments required")
                        .default([]),

                    documents: z
                        .array(
                            z.object({
                                id: z.number(),
                                fileName: z.string(),
                                status: z.enum(["success", "uploading", "error"]),
                                uploadedAt: z.string().optional(),
                            })
                        )
                        .default([]),
                    references: z.map(z.string(), referenceSchema).default(new Map()),
                }),

                // Child Support
                z.object({
                    id: z.string().default(() => Math.random().toString(36).substr(2, 9)),
                    applicantId: z.string(),
                    incomeType: z.literal("Child Support"),

                    isRegisteredAgreement: z.boolean(),
                    childAge: z
                        .number()
                        .int()
                        .min(0, "Child age must be positive")
                        .max(12, "Child must be under 13 years old"),
                    agreementAmount: z.number().min(0, "Agreement amount must be positive"),
                    paymentFrequency: z.enum(paymentFrequencies),
                    continuousPaymentsStartDate: z
                        .string()
                        .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format"),

                    documents: z
                        .array(
                            z.object({
                                id: z.number(),
                                fileName: z.string(),
                                status: z.enum(["success", "uploading", "error"]),
                                uploadedAt: z.string().optional(),
                            })
                        )
                        .default([]),
                    references: z.map(z.string(), referenceSchema).default(new Map()),
                }),

                // Investment
                z.object({
                    id: z.string().default(() => Math.random().toString(36).substr(2, 9)),
                    applicantId: z.string(),
                    incomeType: z.literal("Investment"),

                    investmentName: z
                        .string()
                        .min(1, "Investment name is required")
                        .max(100, "Investment name must be less than 100 characters"),

                    paymentsReceived: z
                        .array(
                            z.object({
                                id: z.string().default(() => crypto.randomUUID()),
                                date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format"),
                                amount: z.number().min(0, "Amount must be positive"),
                            })
                        )
                        .min(1, "At least one payment required")
                        .default([]),

                    documents: z
                        .array(
                            z.object({
                                id: z.number(),
                                fileName: z.string(),
                                status: z.enum(["success", "uploading", "error"]),
                                uploadedAt: z.string().optional(),
                            })
                        )
                        .default([]),
                    references: z.map(z.string(), referenceSchema).default(new Map()),                        
                }),

                // Superannuation
                z.object({
                    id: z.string().default(() => Math.random().toString(36).substr(2, 9)),
                    applicantId: z.string(),
                    incomeType: z.literal("Superannuation"),

                    investmentName: z
                        .string()
                        .min(1, "Superannuation fund name is required")
                        .max(100, "Fund name must be less than 100 characters"),

                    paymentsReceived: z
                        .array(
                            z.object({
                                id: z.string().default(() => crypto.randomUUID()),
                                date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format"),
                                amount: z.number().min(0, "Amount must be positive"),
                            })
                        )
                        .min(2, "Minimum of two payments required")
                        .default([]),

                    documents: z
                        .array(
                            z.object({
                                id: z.number(),
                                fileName: z.string(),
                                status: z.enum(["success", "uploading", "error"]),
                                uploadedAt: z.string().optional(),
                            })
                        )
                        .default([]),
                    references: z.map(z.string(), referenceSchema).default(new Map()),
                }),

                // Defence Income (Reserve)
                z.object({
                    id: z.string().default(() => Math.random().toString(36).substr(2, 9)),
                    applicantId: z.string(),
                    incomeType: z.literal("Defence Income (Reserve)"),

                    verificationMethod: z.enum(defenceVerificationMethods),
                    totalIncomeLast12Months: z.number().min(0, "Total income must be positive").optional(),

                    documents: z
                        .array(
                            z.object({
                                id: z.number(),
                                fileName: z.string(),
                                status: z.enum(["success", "uploading", "error"]),
                                uploadedAt: z.string().optional(),
                            })
                        )
                        .default([]),
                    references: z.map(z.string(), referenceSchema).default(new Map()),
                }),

                // Self-employed
                z.object({
                    id: z.string().default(() => Math.random().toString(36).substr(2, 9)),
                    applicantId: z.string(),
                    incomeType: z.literal("Self-employed"),

                    businessStructure: z.enum(businessStructures, {
                        errorMap: () => ({ message: "Please select a valid business structure" }),
                    }),

                    businessAbn: z
                        .string()
                        .min(1, "Business ABN is required")
                        .regex(abnRegex, "Please enter a valid 11-digit ABN"),

                    businessName: z
                        .string()
                        .min(1, "Business name is required")
                        .max(100, "Business name must be less than 100 characters"),

                    currency: z.string().min(3).max(3).default("AUD"),

                    // Financial year end date
                    financialYearEnd: z
                        .string()
                        .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format")
                        .optional(),

                    // Taxable income and taxes
                    taxableIncome: z.number().optional(),
                    taxesOnIncome: z.number().optional(),

                    documents: z
                        .array(
                            z.object({
                                id: z.number(),
                                fileName: z.string(),
                                status: z.enum(["success", "uploading", "error"]),
                                uploadedAt: z.string().optional(),
                            })
                        )
                        .default([]),
                    references: z.map(z.string(), referenceSchema).default(new Map()),
                }),
            ])
        )
        .default([]),

    // TODO: This could be much better but we are not sure yet of the
    // exact types and fields of expenses, to be improved once we have more
    // clarity on the requirements

    expenses: z.array(
        z.object({
            id: z.string(),
            expenseType: z.enum([
                "General Living Expenses",
                "Childcare Costs",
                "Vehicle Running Costs",
                "Rental / Board Expenses",
                "Investment Property Expense",
                "Other Non-Benchmark Expenses",
                "ATO Tax Arrears",
                "Expected Financial Position Changes",
                "Retirement-Related Expenses",
            ]),
            applicantId: z.string(),
        }).passthrough()
    ).default([]),
});

export default loanApplicationSchema;

// Type inference for TypeScript
export type LoanApplicationData = z.infer<typeof loanApplicationSchema>;

interface NbiFormDefaults {
    citizenship?: string;
    country?: string;
    state?: string;
}

export const useNbiForm = (defaults?: NbiFormDefaults) => {
    return useForm({
        defaultValues: {
            applicationId: undefined,
            submitted: false,
            submissionId: undefined,
            apiSubmitted: false,
            outcome: undefined,
            isLoadingOutcome: false,
            hasOutcomeTimedOut: false,
            productType: "home-loan" as const,
            goalType: "buying-new-property" as const,
            purposeOfLoan: "home-purchase" as const,
            consentProvision: "now" as const,
            consentDocument: [],
            consentType: undefined,
            existingConsentDocument: [],
            propertyAddress: "",
            amounts: {
                propertyMarketValue: 0,
                loanAmount: 0,
                downPayment: 0,
                downPaymentPercentage: 0,
            },
            documents: [],
            uploadStates: [],
            givenNames: "",
            familyNames: "",
            dateOfBirth: "",
            citizenship: (defaults?.citizenship ?? "Australian"),
            phoneNumber: "",
            postCode: "",
            country: (defaults?.country ?? "Australia"),
            suburb: "",
            state: (defaults?.state ?? "NSW"),
            addressLine1: "",
            addressLine2: "",
            proofOfIdentity: "Driver's License" as const,
            idValue: "",
            applicants: [],
            employments: [],
            employerName: "",
            businessAbn: "",
            employmentType: "",
            jobTitle: "",
            employmentStartDate: "",
            employmentEndDate: "",
            incomes: [],
            expenses: [],
        },
        onSubmit: async ({ value }) => {
            // Do something with form data
            console.log(value);
        },
    });
};
